import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import clsx from 'clsx'

import Checkbox from './Checkbox'
import useClickOutside from '../../hooks/useClickOutside'

import './MultiSelectInput.scss'

// pass all props to the input element and define the props type

export type TPropsMultiSelectInput = {
  options: string[]
  selectedOptions: string[]
  onSelectedOptionsChange: (options: string[]) => void
  postfix?: string
  label?: string
  placeholder?: string
  error?: string
  info?: string
  required?: boolean
  numberOfLabelToShow?: number
  enableCustomOption?: boolean
  flexibleWidth?: boolean
  useAllSelectedLabel?: boolean
  disableSelectAllOptionButton?: boolean
}

const MultiSelectInput = (props: TPropsMultiSelectInput & React.InputHTMLAttributes<HTMLInputElement>) => {
  const {
    label,
    placeholder,
    required,
    options,
    selectedOptions,
    onSelectedOptionsChange,
    numberOfLabelToShow = 2,
    enableCustomOption = false,
    flexibleWidth,
    useAllSelectedLabel = false,
    disableSelectAllOptionButton = false
  } = props
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const numberOfOptionsThatHidden = selectedOptions.length - numberOfLabelToShow
  const haveHiddenOptions = numberOfOptionsThatHidden > 0

  const selectedOptionsShown = selectedOptions.slice(0, numberOfLabelToShow)

  const handleClickOutside = () => {
    setSearchQuery('')
    setIsOpen(false)
  }

  useClickOutside(wrapperRef, handleClickOutside)

  useEffect(() => {
    if (isOpen) {
      // Focus the search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  const handleOptionClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>, option: string) => {
      e.preventDefault()
      e.stopPropagation()
      if (selectedOptions.includes(option)) {
        onSelectedOptionsChange(selectedOptions.filter(o => o !== option))
      } else {
        onSelectedOptionsChange([...selectedOptions, option])
      }
    },
    [onSelectedOptionsChange, selectedOptions]
  )

  const renderedLabel = props.label ? (
    <label className="tq-input-label">
      {label}
      {required && <span className="tq-input-mandatory">*</span>}
    </label>
  ) : null

  const showSelectedAllLabel = useMemo(() => {
    return useAllSelectedLabel && (selectedOptions.length === options.length || selectedOptions.length === 0)
  }, [selectedOptions, options, useAllSelectedLabel])

  const filteredOptions = useMemo(() => {
    return options.filter(option => option.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [options, searchQuery])

  return (
    <div className="tq-input-wrapper tq-multi-select" ref={wrapperRef}>
      {renderedLabel}
      <div className={clsx('tq-input-field', { 'flexible-width': flexibleWidth })} onClick={() => setIsOpen(!isOpen)}>
        {showSelectedAllLabel && <span className="tq-input-all-selected">All selected</span>}
        {!showSelectedAllLabel && (
          <div className="tq-tag-container">
            {selectedOptions.length === 0 && <span className="placeholder">{placeholder}</span>}
            {selectedOptionsShown.map(option => (
              <div className="tq-tag" key={option}>
                {option}
                {/* close icon */}
                <div className="close-icon" onClick={e => handleOptionClick(e, option)}>
                  <i className="fa fa-times" />
                </div>
              </div>
            ))}
            {haveHiddenOptions && <div className="tq-tag tq-tag-more">+{numberOfOptionsThatHidden}</div>}
          </div>
        )}
        {props.postfix && <span className="tq-input-postfix">{props.postfix}</span>}
        {/* chevron down icon */}
        <div className="chevron-down-icon" onClick={() => setIsOpen(!isOpen)}>
          <i className="fa fa-chevron-down" />
        </div>
      </div>
      {props.error && <span className="tq-input-error">{props.error}</span>}
      {props.info && <span className="tq-input-info">{props.info}</span>}
      {isOpen && (
        <div className="tq-input-options">
          {/* Search */}
          <div className="tq-input-search-wrapper">
            <div className="tq-input-search-icon">
              <i className="fa fa-search" />
            </div>
            <input
              type="text"
              className="tq-input-search"
              ref={searchInputRef}
              placeholder={enableCustomOption ? 'Search or add new option' : 'Search'}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => {
                if (!enableCustomOption) return
                if (e.key === 'Enter') {
                  e.preventDefault()
                  const value = searchQuery.trim()
                  if (!value) return
                  const exactExists = options.includes(value)
                  if (!exactExists) {
                    if (!selectedOptions.includes(value)) {
                      onSelectedOptionsChange([...selectedOptions, value])
                    }
                    setSearchQuery('')
                  }
                }
              }}
              value={searchQuery}
            />
            {/* close icon */}
            {searchQuery && (
              <div className="close-icon" onClick={() => setSearchQuery('')}>
                <i className="fa fa-times" />
              </div>
            )}
          </div>
          {/* Select all */}
          {options.length > 0 && selectedOptions.length === options.length && !disableSelectAllOptionButton && (
            <div className="tq-input-option" onClick={() => onSelectedOptionsChange([])}>
              <span className="tq-input-option-label deselect-all">Deselect all</span>
            </div>
          )}
          {options.length > 0 && selectedOptions.length < options.length && !disableSelectAllOptionButton && (
            <div className="tq-input-option" onClick={() => onSelectedOptionsChange(options)}>
              <span className="tq-input-option-label select-all">Select all</span>
            </div>
          )}
          {/* options */}
          {filteredOptions.map(option => (
            <div
              className={clsx('tq-input-option', { selected: selectedOptions.includes(option) })}
              key={option}
              onClick={e => handleOptionClick(e, option)}
            >
              <Checkbox label={option} checked={selectedOptions.includes(option)} />
            </div>
          ))}
          {/* empty options */}
          {options.length === 0 ||
            (filteredOptions.length === 0 && searchQuery && (
              <div className="tq-input-no-option">
                <span className="tq-input-option-label">
                  {!enableCustomOption ? 'No options' : 'Press enter to add new option'}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

export default MultiSelectInput
